import {createContext, useReducer} from 'react';
import AuthReducer from './AuthReducer';
import React, {useEffect, useState} from 'react';
import { getUser } from '../apiCalls';
import PropTypes from 'prop-types';
import axios from 'axios';
import config from '../config';
import CallbackPopup from '../components/popup/CallbackPopup';
import { startSession } from '../analytics/sessionId';
import { io } from "socket.io-client";
import { openDB } from 'idb';
import { SetDBError } from './AuthActions';

const INITIAL_STATE = {
    user: null,
    session: null,
    balance: 0,
    socket_context: null,
    imageCache: new Map(),
    updateBalance: () => {},
    error:false,
    isLoading: true,
    indexedDB: null,
};

export const AuthContext = createContext(INITIAL_STATE);

export const AuthContextProvider = ({children}) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('auth context popup');
  const [db, setDB] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  // initialize the indexed DB
  useEffect(() => {
    const initIndexedDB = async () => {
      try {
        const db = await openDB('ImageDB', 2, {
          upgrade(db) {
            if (!db.objectStoreNames.contains('shareImages')) {
              db.createObjectStore('shareImages', { keyPath: 'username' });
            }
            if (!db.objectStoreNames.contains('wishlistImages')) {
              db.createObjectStore('wishlistImages', { keyPath: 'username' });
            }
          },
        });
        setDB(db);
        console.log("IndexedDB initialized");
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error);
        dispatch(SetDBError(error));
      }
    };
    initIndexedDB();
  }, []);

  // share images, not wishlist item images
  const addImagesToDB = async (images, username) => {
    try {
        if (!db) {
            console.error("DB not initialized");
            throw new Error("DB not initialized");
        }
        const transaction = db.transaction(['shareImages'], 'readwrite');
        const store = transaction.objectStore('shareImages');
        await store.delete(username);

        const userImages = { username, images };
        await store.put(userImages);
    } catch (error) {
        console.error("Error adding images to DB:", error);
        dispatch({type: 'SET_DB_ERROR', payload: error});
    }
}

const addWishlistImagesToDB = async (images, username) => {
  try {
      if (!db) {
          console.error("DB not initialized");
          throw new Error("DB not initialized");
      }
      const transaction = db.transaction(['wishlistImages'], 'readwrite');
      const store = transaction.objectStore('wishlistImages');
      await store.delete(username);
      
      const userImages = { username, images };
      await store.put(userImages);
  } catch (error) {
      console.error("Error adding wishlist images to DB:", error);
      dispatch({type: 'SET_DB_ERROR', payload: error});
  }
}

const fetchWishlistImagesFromDB = async (username, itemName) => {
  if(!db){
    console.error("DB not initialized, cannot fetch.");
    return null;
  }
  try {
    const transaction = db.transaction(['wishlistImages'], 'readonly');
    const store = transaction.objectStore('wishlistImages');
    const result = await store.get(username);

    if(result && Array.isArray(result.images)){
      const image = result.images.find(img => img.itemName === itemName);
      if(image){
        return image.url;
      } else {
      console.warn("Wishlist image for item name:", itemName, "not found for ", username );
      return null;
    } 
    } else {
      console.warn("No images found for user: ", username);
    }
  } catch (error) {
    console.error("Error fetching wishlist images from Indexed DB: ", error);
    dispatch({ type: 'SET_DB_ERROR', payload: error});
    return null;
  }
}

const fetchShareImagesFromDB = async (username, templateIndex) => {
  if(!db){
    console.error("DB not initialized, cannot fetch.");
    return [];
  }
  try {
    const transaction = db.transaction(['shareImages'], 'readonly');
    const store = transaction.objectStore('shareImages');
    const result = await store.get(username);
    if(result && Array.isArray(result.images) && result.images[templateIndex] !== undefined){
      return result.images[templateIndex];
    } else {
      console.warn("Image at index", templateIndex, "not found for ", username );
      return null;
    }
  } catch (error) {
    console.error("Error fetching images from Indexed DB: ", error);
    dispatch({ type: 'SET_DB_ERROR', payload: error});
    return [];
  }
}

  // useEffect to handle error messages and display popups
  useEffect(() => {
    if (state.error) {
      setPopupMessage(state.error);
      setShowPopup(true);
    }
  }, [state.error]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if(token){
      const fetchUser = async () => {
        await getUser(dispatch);
        dispatch({ type: 'SET_LOADING', payload: false });
      };
      fetchUser();
    } else {
        dispatch({type: 'SET_LOADING', payload: false});
    }
      
  }, [dispatch]);

  useEffect(() => {
    if(!state.user) return;
    console.log("state.user", state.user);
    const id = startSession(state.user._id);
    setSessionId(id);
  }, [state.user])

  // connect socket, stored in context
  useEffect(() => {
    if (!state.user || !state.user._id) return;
    const sessionId = localStorage.getItem('sessionId');
    const newSocket = io(process.env.REACT_APP_API_URL, {
      reconnectionDelayMax: 2000,
      maxDisconnectionDuration: Infinity,
      query: {
        sessionId
      }
    });

    dispatch({ type: 'SET_SOCKET', payload: newSocket});
    
    newSocket.on("connect", () => {
        // newSocket.on('init');
        newSocket.emit("newUser", state.user._id);
    });

    return () => {
      newSocket.off("connect");
      newSocket.off("reconnect");
      newSocket.close();
      if (state.socket_context) {
        state.socket_context.close();
        dispatch({ type: 'REMOVE_SOCKET' });
      }
    }

  }, [state.user]);

  const [balance, setNewBalance] = useState(0);

  // this doesn't really update the balance, it just retreives the balance
  const updateBalance = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setNewBalance(0); // set balance to 0 with no token
      return;
    }
    try { 
      const response = await axios.get(`${config.apiUrl}/api/billing-settings-balance`, {
        headers: {
          "x-auth-token": token,
        },
      });

      let newBalance = parseFloat(response.data.balance);

      if (isNaN(newBalance)) {
        newBalance = 0;
      }
      setNewBalance(newBalance);
    } catch (error) {
      alert("Error fetching user balance.");
    }
  };

return (
  <AuthContext.Provider 
    value={{
      user:state.user,
      sessionId: sessionId,
      balance: balance,
      socket_context: state.socket_context,
      imageCache: state.imageCache,
      updateBalance: updateBalance,
      error:state.error,
      isLoading: state.isLoading,
      dispatch,
      indexedDB: db,
      addImagesToDB: addImagesToDB,
      fetchShareImagesFromDB: fetchShareImagesFromDB,
      addWishlistImagesToDB: addWishlistImagesToDB, 
      fetchWishlistImagesFromDB: fetchWishlistImagesFromDB
    }}
  >
    {showPopup && <CallbackPopup message={popupMessage} onClose={() => setShowPopup(false)} />}
    {children}
  </AuthContext.Provider>
);
};

AuthContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};